import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, Row, message } from 'antd';
import { simplePost } from 'common/utils/http';
import { AttributeNameValPair, DerivedAttributePair } from './interfaces';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialNaturalAttr?: AttributeNameValPair;
}

const createDerivedAttribute: (attrPair: DerivedAttributePair) => Promise<void> = attrPair =>
  simplePost('/api/v1/derived', attrPair);

const validateAttr: (attr: AttributeNameValPair) => string | null = ({
  name,
  value,
}: AttributeNameValPair) => {
  if (name.length === 0) {
    return 'name cannot be blank';
  }

  if (value.length === 0) {
    return 'value cannot be blank';
  }

  return null;
};

const CreateDerivedAttributeModal = ({ isOpen, onClose, initialNaturalAttr }: Props) => {
  const [naturalAttribute, setNaturalAttribute] = useState<AttributeNameValPair>(
    initialNaturalAttr as AttributeNameValPair
  );
  const [derivedAttribute, setDerivedAttribute] = useState<AttributeNameValPair>(
    {} as AttributeNameValPair
  );
  useEffect(
    () => setNaturalAttribute(initialNaturalAttr as AttributeNameValPair),
    [initialNaturalAttr]
  );

  const onNaturalAttrChange = (field: 'name' | 'value', value: string) => {
    setNaturalAttribute({ ...naturalAttribute, [field]: value });
  };
  const onDerivedAttrChange = (field: 'name' | 'value', value: string) => {
    setDerivedAttribute({ ...derivedAttribute, [field]: value });
  };

  const onCloseModal = () => {
    onClose();
    setNaturalAttribute({} as AttributeNameValPair);
    setDerivedAttribute({} as AttributeNameValPair);
  };

  const onSubmit = async () => {
    const naturalAttrError = validateAttr(naturalAttribute);
    if (naturalAttrError) {
      message.error(`Natural Attribute Invalid: ${naturalAttrError}`);
      return;
    }
    const derivedAttrError = validateAttr(derivedAttribute);
    if (naturalAttrError) {
      message.error(`Derived Attribute Invalid: ${derivedAttrError}`);
      return;
    }

    try {
      await createDerivedAttribute({ attribute: naturalAttribute, derived: derivedAttribute });
      onCloseModal();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (e: any) {
      message.error(e.error);
    }
  };

  return (
    <Modal
      footer={[
        <Button key="cancel" onClick={onCloseModal} type="link">
          Cancel
        </Button>,
        <Button key="submit" onClick={onSubmit} type="primary">
          Save
        </Button>,
      ]}
      onCancel={onCloseModal}
      open={isOpen}
      title="Create Derived Attribute"
    >
      <Form labelCol={{ span: 6 }} layout="horizontal" wrapperCol={{ span: 18 }}>
        <Form.Item label="Natural Attribute">
          <Input.Group>
            <Row>
              <Input
                onChange={e => onNaturalAttrChange('name', e.target.value)}
                placeholder="name"
                style={{ width: '45%', marginRight: '10px' }}
                value={naturalAttribute?.name}
              />
              <Input
                onChange={e => onNaturalAttrChange('value', e.target.value)}
                placeholder="value"
                style={{ width: '45%' }}
                value={naturalAttribute?.value}
              />
            </Row>
          </Input.Group>
        </Form.Item>
        <Form.Item label="Derived Attribute">
          <Input.Group>
            <Row>
              <Input
                onChange={e => onDerivedAttrChange('name', e.target.value)}
                placeholder="name"
                style={{ width: '45%', marginRight: '10px' }}
                value={derivedAttribute?.name}
              />
              <Input
                onChange={e => onDerivedAttrChange('value', e.target.value)}
                placeholder="value"
                style={{ width: '45%' }}
                value={derivedAttribute?.value}
              />
            </Row>
          </Input.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateDerivedAttributeModal;
